class DrawerCartNote extends HTMLElement {
  constructor() {
    super();

    this.triggers = [
      this.querySelector(".giftnote__drawercart__addnote"),
      this.querySelector(".giftnote__body__close"),
      this.querySelector(".giftnote__overlay"),
    ];
    this.saveBtn = this.querySelector(".giftnote__save");

    this.addEventListener(
      "change",
      debounce((event) => {
        const body = JSON.stringify({
          note: document.getElementById("CartDrawer-note").value,
        });
        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }, ON_CHANGE_DEBOUNCE_TIMER),
    );

    this.onToggle.bind(this);
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, textarea, [tabindex]";
    const focusableElements = () =>
      Array.from(
        this.querySelector(".giftnote__body").querySelectorAll(
          focusableElementsSelector,
        ),
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  connectedCallback() {
    this.init();
  }

  onSave() {
    const body = JSON.stringify({
      note: document.getElementById("CartDrawer-note").value,
    });
    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
    this.onToggle();
  }

  syncNoteLabel() {
    if (!this.noteLink || !this.noteTextarea) return;
    const addLabel =
      this.noteLink.dataset.addLabel || this.noteLink.textContent.trim();
    const editLabel = this.noteLink.dataset.editLabel || addLabel;
    this.noteLink.textContent =
      this.noteTextarea.value.trim().length > 0 ? editLabel : addLabel;
  }

  onToggle() {
    const { focusableElements } = this.getFocusableElements();

    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      setTabindex(focusableElements(), "-1");
      this.triggers[0].focus();
    } else {
      this.setAttribute("open", true);
      setTabindex(focusableElements(), "0");
      this.triggers[1].focus();
    }
  }

  init() {
    this.triggers.forEach((trigger) => {
      trigger?.addEventListener("click", (e) => {
        e.preventDefault();
        this.onToggle();
      });
    });

    this.saveBtn.addEventListener("click", (e) => {
      e.preventDefault();
      this.onSave();
    });

    // ADD/EDIT trigger-label toggle. Runs in init() (connectedCallback) so it
    // re-binds + re-syncs every time a new <drawer-cart-note> is connected —
    // including after the cart drawer re-renders via the section-rendering API,
    // which replaces these nodes and would orphan a one-time DOMContentLoaded
    // listener. syncNoteLabel() on connect also fixes the initial label when the
    // re-rendered markup ships the default "add" text but cart.note is non-empty.
    this.noteLink = this.triggers[0];
    this.noteTextarea = this.querySelector("#CartDrawer-note");
    this.syncNoteLabel();
    this.noteTextarea?.addEventListener("input", () => this.syncNoteLabel());

    // W2-25 · "Is this a gift?" checkbox.
    // Ticking it sets the order-level `is_gift` attribute (→ Shopify Flow adds
    // the `gift-note` tag even with no message) and reveals the note field.
    // Unticking clears the attribute AND the note so the order is fully not-a-gift.
    // One atomic /cart/update.js POST carries both note + attribute (no race);
    // stopPropagation() keeps the generic note-save change listener from
    // double-firing. State (checked + .is-gift) is rendered from cart.attributes
    // in Liquid, so it survives the section re-renders that replace these nodes.
    this.giftCheckbox = this.querySelector("#CartDrawer-isGift");
    if (this.giftCheckbox) {
      this.giftCheckbox.addEventListener("change", (e) => {
        e.stopPropagation();
        const isGift = this.giftCheckbox.checked;
        this.classList.toggle("is-gift", isGift);

        if (!isGift && this.noteTextarea) this.noteTextarea.value = "";
        const note = isGift && this.noteTextarea ? this.noteTextarea.value : "";

        const body = JSON.stringify({
          note: note,
          attributes: { is_gift: isGift ? "Yes" : "" },
        });
        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });

        this.syncNoteLabel();
        if (!isGift && this.hasAttribute("open")) this.onToggle();
      });
    }

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.onToggle();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });
  }
}

customElements.define("drawer-cart-note", DrawerCartNote);
